<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { CreditLedgerEntry, CreditLedgerType } from '@/types/newsletter'
import { CreditLedgerType as LedgerType } from '@/types/newsletter'
import type { PaginationMeta } from '@/types/finance'
import PaginationControls from '@/components/shared/PaginationControls.vue'
import PerPageSelect from '@/components/shared/PerPageSelect.vue'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    entries: CreditLedgerEntry[]
    loading: boolean
    meta: PaginationMeta | null
    page: number
    perPage?: number
  }>(),
  { perPage: 25 },
)

const emit = defineEmits<{
  'update:page': [page: number]
  'update:perPage': [perPage: number]
}>()

function typeLabel(type: CreditLedgerType): string {
  const map: Record<CreditLedgerType, string> = {
    earned:    t('credits.ledger.type_earned'),
    purchased: t('credits.ledger.type_purchased'),
    consumed:  t('credits.ledger.type_consumed'),
    refunded:  t('credits.ledger.type_refunded'),
    adjusted:  t('credits.ledger.type_adjusted'),
  }
  return map[type] ?? type
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function referenceLabel(entry: CreditLedgerEntry): string {
  if (entry.reference_type === 'newsletter') return t('credits.ledger.reference_newsletter')
  if (entry.reference_type === 'registration') return t('credits.ledger.reference_registration')
  if (entry.reference_type === 'credit_purchase') return t('credits.ledger.reference_credit_purchase')
  return '—'
}
</script>

<template>
  <div>
    <!-- Toolbar: per-page selector -->
    <div v-if="entries.length > 0" class="flex flex-wrap items-center gap-3 mb-4">
      <PerPageSelect
        :model-value="props.perPage"
        @update:model-value="emit('update:perPage', $event)"
      />
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading && entries.length === 0" class="space-y-2">
      <div
        v-for="i in 6"
        :key="i"
        class="rounded-lg bg-slate-800/50 border border-slate-700 px-4 py-3 animate-pulse"
      >
        <div class="flex justify-between">
          <div class="h-4 bg-slate-700 rounded w-48" />
          <div class="h-4 bg-slate-700 rounded w-20" />
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="entries.length === 0"
      class="rounded-xl border border-dashed border-slate-700 px-8 py-12 text-center"
    >
      <p class="text-slate-400 text-sm">{{ t('credits.ledger.empty') }}</p>
    </div>

    <!-- Ledger table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead>
          <tr class="border-b border-slate-700">
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('credits.ledger.column_type') }}</th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('credits.ledger.column_description') }}</th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">{{ t('credits.ledger.column_reference') }}</th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">{{ t('credits.ledger.column_amount') }}</th>
            <th class="px-3 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">{{ t('credits.ledger.column_date') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="entry in entries"
            :key="entry.id"
            class="border-b border-slate-700/50 hover:bg-slate-800/50 transition-colors"
          >
            <!-- Type badge -->
            <td class="px-3 py-3">
              <span
                class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border"
                :class="{
                  'bg-emerald-500/15 border-emerald-500/30 text-emerald-400': entry.type === LedgerType.Earned || entry.type === LedgerType.Purchased || entry.type === LedgerType.Refunded || (entry.type === LedgerType.Adjusted && entry.amount >= 0),
                  'bg-red-500/15 border-red-500/30 text-red-400': entry.type === LedgerType.Consumed || (entry.type === LedgerType.Adjusted && entry.amount < 0),
                }"
              >
                {{ typeLabel(entry.type) }}
              </span>
            </td>
            <td class="px-3 py-3 text-slate-300 truncate max-w-xs">
              {{ entry.description ?? '—' }}
            </td>
            <td class="px-3 py-3 text-slate-400 text-xs">
              {{ referenceLabel(entry) }}
            </td>
            <!-- Amount: positive green, negative red -->
            <td class="px-3 py-3 text-right font-mono font-medium"
              :class="entry.amount >= 0 ? 'text-emerald-400' : 'text-red-400'"
            >
              {{ entry.amount >= 0 ? '+' : '' }}{{ entry.amount.toLocaleString() }}
            </td>
            <td class="px-3 py-3 text-right text-slate-400 text-xs whitespace-nowrap">
              {{ formatDate(entry.created_at) }}
            </td>
          </tr>
        </tbody>
      </table>

      <PaginationControls
        v-if="meta"
        :current-page="page"
        :total-pages="meta.last_page"
        @change="emit('update:page', $event)"
      />
    </div>
  </div>
</template>
